import { useEffect, useState } from "react";
import api from "@/lib/api";
import Tip from "@/components/Tip";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { HeartPulse, Clock, PlayCircle, RefreshCw, Send, Mail, MessageCircle, Sparkles, MessageSquareWarning, Repeat } from "lucide-react";

const BRAND_BLUE = "#004a99";
const BRAND_ORANGE = "#f37021";

/** Maps cron-job id → visual config (brand palette only) */
const JOB_META = {
  payment_reminders_daily:   { label: "Payment Reminders", icon: Send, accent: BRAND_BLUE, time: "10:00 IST", desc: "WhatsApp + Email to pending invoices" },
  synced_followup_24h:       { label: "24h Synced Follow-up", icon: HeartPulse, accent: BRAND_ORANGE, time: "11:00 IST", desc: "Condition-specific check-in" },
  referral_nudge_48h:        { label: "48h Referral Nudge", icon: Sparkles, accent: BRAND_BLUE, time: "12:00 IST", desc: "Post-recovery referral ask" },
  automated_recall:          { label: "Automated Recall", icon: Repeat, accent: BRAND_ORANGE, time: "18:00 IST", desc: "1 day before EMR follow-up" },
};

const JOB_ID_FROM_RUN = {
  payment_reminders: "payment_reminders_daily",
  synced_followup_24h: "synced_followup_24h",
  referral_nudge_48h: "referral_nudge_48h",
  automated_recall: "automated_recall",
  humanized_journey_manual: null,
};

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—";

function countdown(iso) {
  if (!iso) return "—";
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return "running…";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function JourneyRunsWidget() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);

  const load = async () => {
    try {
      const r = await api.get("/super-admin/cron/status");
      setData(r.data);
    } catch { setData({ error: true }); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const triggerAll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/super-admin/journey/trigger-now");
      const s = `Sync ${r.data.synced_followup.sent} · Recall ${r.data.automated_recall.sent} · Referral ${r.data.referral_nudge.sent}`;
      toast.success(`Journey triggered · ${s}`);
      await load();
    } catch { toast.error("Trigger failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="card-3d bg-white rounded-2xl p-5 border-2" style={{ borderColor: `${BRAND_ORANGE}30` }}>Loading journey runs…</div>;
  if (data.error) return <div className="card-3d bg-white rounded-2xl p-5 border-2 border-red-200">Failed to load.</div>;

  // Show all 4 cron jobs with their meta
  const jobs = (data.jobs || []).map(j => ({ ...j, meta: JOB_META[j.id] || { label: j.name, icon: Clock, accent: BRAND_BLUE, time: "—", desc: "" } }));
  const recent = (data.recent_runs || []).slice(0, 6);

  return (
    <div className="card-3d bg-white rounded-2xl p-5 border-2 fade-up" style={{ borderColor: `${BRAND_ORANGE}30` }} data-testid="journey-runs-widget">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="icon-3d icon-float h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: `${BRAND_ORANGE}15` }}>
            <HeartPulse className="h-5 w-5" style={{ color: BRAND_ORANGE }} />
          </div>
          <div>
            <h2 className="font-heading font-bold text-base flex items-center" style={{ color: BRAND_BLUE }}>
              Humanized Patient Journey
              <Tip text="Daily AI-powered touchpoints across every clinic. Welcome → 24h follow-up → recall → referral ask. All wrapped with GPT-4o-mini humanizer." />
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">4 jobs · GPT-4o-mini + Twilio + Resend</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={load} className="text-slate-400 hover:text-slate-700 p-1.5 rounded hover:bg-slate-100" data-testid="journey-refresh" title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Job grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-4">
        {jobs.map(j => {
          const Icon = j.meta.icon;
          return (
            <div key={j.id} className="rounded-xl p-3 border-2" style={{ borderColor: `${j.meta.accent}20`, background: `${j.meta.accent}05` }} data-testid={`journey-job-${j.id}`}>
              <div className="flex items-start gap-2.5">
                <div className="icon-3d h-9 w-9 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ background: `${j.meta.accent}15` }}>
                  <Icon className="h-4 w-4" style={{ color: j.meta.accent }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm text-slate-900 leading-tight truncate">{j.meta.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{j.meta.desc}</div>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    <span className="text-[10px] uppercase font-semibold tracking-wide px-1.5 py-0.5 rounded" style={{ background: `${j.meta.accent}15`, color: j.meta.accent }}>{j.meta.time}</span>
                    <span className="text-[10px] text-slate-500">next in <b style={{ color: j.meta.accent }}>{countdown(j.next_run)}</b></span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Button onClick={triggerAll} disabled={busy} size="sm" className="w-full text-white btn-sheen mb-4" style={{ background: BRAND_ORANGE }} data-testid="journey-trigger-all">
        <PlayCircle className="h-4 w-4 mr-1.5" /> {busy ? "Running all 3…" : "Run all journey jobs now"}
      </Button>

      {/* Recent runs */}
      <div>
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1">
          <Send className="h-3 w-3" /> Recent journey runs
        </div>
        {recent.length === 0 ? (
          <div className="text-xs text-slate-400 py-3 text-center bg-slate-50 rounded-lg" data-testid="journey-no-runs">No runs yet — they'll fire automatically, or tap "Run now".</div>
        ) : (
          <div className="space-y-1.5 max-h-44 overflow-y-auto">
            {recent.map((r, i) => {
              const jobLabel = JOB_META[JOB_ID_FROM_RUN[r.job] || r.job]?.label || r.job;
              const totalSent = r.sent ?? ((r.synced_followup?.sent || 0) + (r.automated_recall?.sent || 0) + (r.referral_nudge?.sent || 0) + (r.whatsapp_sent || 0));
              return (
                <div key={i} className="flex items-center justify-between text-xs px-3 py-2 rounded-lg border" style={{ borderColor: `${BRAND_BLUE}20`, background: i === 0 ? `${BRAND_BLUE}07` : "#fff" }} data-testid={`journey-run-${i}`}>
                  <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                    <span className="font-semibold truncate" style={{ color: BRAND_BLUE }}>{jobLabel}</span>
                    {r.manual && <span className="text-[10px] uppercase tracking-wide px-1.5 rounded-full flex-shrink-0" style={{ background: `${BRAND_ORANGE}20`, color: BRAND_ORANGE }}>manual</span>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-slate-500">{fmtDT(r.ran_at)}</span>
                    <span className="flex items-center gap-0.5 font-semibold" style={{ color: BRAND_ORANGE }}><MessageCircle className="h-3 w-3" />{totalSent}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
